<template>
  <div class="image-viewer">
    <div ref="canvas-wrap" class="canvas-wrap">
      <canvas ref="canvas" class="main-canvas" />
        <span class="image-viewer-counter" v-if="showCounter">{{ imageIndex + 1 }} / {{ images.length }}</span>
        <span class="image-viewer-name" v-if="showName">{{ activeImage.name }}</span>
    </div>
    <div class="thumbs-wrap">
      <button v-if="prevArrowVisible" class="thumbs-arrow left" @click="prevImage">
        <i class="fa fa-chevron-left"></i></button>
      <div class="thumbs" v-if="visibleThumbs"
        :style="{ 'grid-template-columns': thumbsCount }">
        <div
          v-for="item in visibleThumbs"
          :key="item.index"
          class="thumb"
          :style="{ 'aspect-ratio': thumbsAspect }"
          :class="{ active: item.index === imageIndex }"
          @click="switchImage(item.index)"
        >
          <img :src="item.thumb.url" :alt="item.thumb.name" />
        </div>
      </div>
      <button v-if="nextArrowVisible" class="thumbs-arrow right" @click="nextImage">
        <i class="fa fa-chevron-right" />
      </button>
    </div>
  </div>
</template>

<script>
import * as util from '@/_segmentation_util.js';

export default {
  name: 'ImageViewer',
  props: {
    // An array of image { name, url}
    images: {
      type: Array,
      required: true,
    },
    thumbs: {
      type: Array,
      default: () => [],
    },
    aspect: {
      type: Number,
      default: 1,
    },
    thumbsAspect: {
      type: Number,
      default: 1,
    },
    thumbsCount: {
      type: Number,
      default: 5,
    },
    loopThumbs: {
      type: Boolean,
      default: false,
    },
    showCounter: {
      type: Boolean,
      default: true,
    },
    showName: {
      type: Boolean,
      default: true,
    },
    fileIndex: {
      type: Number,
      default: 0
    }
  },
  data: () => ({
    canvas: null,
    canvasWrap: null,
    ctx: null,
    imageIndex: 0,
    thumbIndex: 0,
    zoom: 1,
    // offset from center
    dx: 0,
    dy: 0,
    canvasWidth: 0,
    canvasHeight: 0,
    imageWidth: 0,
    imageHeight: 0,
    image: null,
    scroll_increment: 2 ** (1 / 5),
    previousMousePosition: null,
  }),
  computed: {
    visibleThumbs() {
      let result = this.thumbs.map((thumb, index) => { return { thumb: thumb, index: index }; });
      result = result.slice(this.thumbIndex, this.thumbIndex + this.thumbsCount);
      return result;
    },
    nextArrowVisible() {
      // return (this.thumbIndex + this.thumbsCount) < this.images.length - 1;
      return true;
    },
    prevArrowVisible() {
      // return this.thumbIndex > 0;
      return true;
    },
    activeImage() {
      return this.images[this.imageIndex];
    },
  },
  mounted() {
    if (this.fileIndex >= 0 && this.fileIndex < this.images.length) {
      this.imageIndex = this.fileIndex;
      this.thumbIndex = Math.min(this.imageIndex - this.thumbsCount / 2, this.images.length - this.thumbsCount);
      this.thumbIndex = Math.max(this.thumbIndex, 0);
    }

    this.canvas = this.$refs.canvas;
    this.canvasWrap = this.$refs['canvas-wrap'];
    this.ctx = this.canvas.getContext('2d');
    window.addEventListener('resize', this.resizeCanvas);
    this.canvasWrap.addEventListener('wheel', this.mouseScrollZoom);
    this.canvasWrap.addEventListener('mousemove', this.mouseMove);
    // arrow keys to navigate images
    window.addEventListener('keydown', this.keydown);

    this.load();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCanvas);
    this.canvasWrap.removeEventListener('wheel', this.mouseScrollZoom);
    this.canvasWrap.removeEventListener('mousemove', this.mouseMove);
    window.removeEventListener('keydown', this.keydown);
  },
  methods: {
    load() {
      this.resizeCanvas();
      this.loadImage();
    },
    switchImage(index) {
      this.imageIndex = index;
      this.dx = 0;
      this.dy = 0;
      this.zoom = 1;
      this.loadImage();
    },
    // resize canvas to fit the container
    resizeCanvas() {
      // Needed so that canvas can actually scale down.
      // Otherwise it will use 100% of height and create overflow when scaled down.
      this.canvas.width = 0;
      this.canvas.height = 0;

      this.canvas.width = this.canvasWrap.clientWidth;
      this.canvas.height = this.canvasWrap.clientHeight;
      this.canvasWidth = this.canvasWrap.clientWidth;
      this.canvasHeight = this.canvasWrap.clientHeight;

      if (this.image) {
        this.calculateZoom();
        this.draw();
      }
    },
    nextImage() {
      if (this.thumbIndex + this.thumbsCount < this.images.length) {
        this.thumbIndex += 1;
      }
    },
    prevImage() {
      if (this.thumbIndex >= 1) {
        this.thumbIndex -= 1;
      }
    },
    keydown(e) {
      if (e.key === 'ArrowRight') {
        this.nextImage();
      } else if (e.key === 'ArrowLeft') {
        this.prevImage();
      }
    },
    getMousePos(event) {
      const { canvas } = this.$refs;
      if (!canvas) {
        return null;
      }

      const rect = canvas.getBoundingClientRect();
      const pos = [
        event.clientX - rect.left - canvas.width / 2,
        event.clientY - rect.top - canvas.height / 2,
      ];
      pos[0] /= this.zoom;
      pos[1] /= this.zoom;
      pos[0] -= this.dx;
      pos[1] -= this.dy;
      return pos;
    },
    // mouse scroll zoom from mouse pointer location
    mouseScrollZoom(e) {
      e.preventDefault();
      this.zoom *= this.scroll_increment ** (-e.deltaY / 100);
      const offset = util.pt_sub(this.getMousePos(e), this.previousMousePosition);

      this.dx += offset[0];
      this.dy += offset[1];

      this.draw();
    },
    mouseMove(e) {
      this.previousMousePosition = [...this.getMousePos(e)];
      if (e.buttons === 1) {
        this.dx += e.movementX / this.zoom;
        this.dy += e.movementY / this.zoom;
        this.draw();
      }
    },
    resetZoomAndPan() {
      this.calculateZoom();
      const centerX = Math.round(this.canvasWidth / 2);
      const centerY = Math.round(this.canvasHeight / 2);
      const halfWidth = Math.round(this.imageWidth * this.zoom) / 2;
      const halfHeight = Math.round(this.imageHeight * this.zoom) / 2;

      this.zoom = 1;
      this.dx = centerX - halfWidth;
      this.dy = centerY - halfHeight;
      this.draw();
    },
    calculateZoom() {
      const zoomX = this.canvasWidth / this.imageWidth;
      const zoomY = this.canvasHeight / this.imageHeight;
      this.zoom = Math.min(zoomX, zoomY);
    },
    // draw the active image
    loadImage() {
      this.clear();
      if (!this.activeImage.mime.startsWith('image')) {
        return;
      }

      const img = new Image();
      img.src = this.activeImage.url;

      img.onload = () => {
        this.imageWidth = img.width;
        this.imageHeight = img.height;
        this.image = img;
        this.calculateZoom();
        this.draw();
      };
    },
    clear() {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.ctx.save();
    },
    draw() {
      this.clear();
      this.ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
      this.ctx.scale(this.zoom, this.zoom);
      this.ctx.translate(-this.imageWidth / 2 + this.dx, -this.imageHeight / 2 + this.dy);
      this.ctx.drawImage(
        this.image,
        0,
        0,
      );
      this.ctx.restore();
    },
  }
};
</script>
<style lang=scss>
.canvas-wrap {
  position: relative;
  width: 100%;
  overflow: hidden;
  height: 100%;
}

.thumbs {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 1rem;
  margin-top: 1rem;
}

.thumb {
  cursor: pointer;
  border: 1px solid #ccc;
  overflow: hidden;
  position: relative;
  aspect-ratio: 1;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.active {
    border-color: #007bff;
    border-width: 3px;
  }
}

.thumbs-wrap {
  position: relative;
}

.thumbs-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.5);
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  transition: background 0.3s;
  z-index: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }

  &.left {
    left: 0;
  }

  &.right {
    right: 0;
  }
}

.image-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.image-viewer-counter {
  position: absolute;
  bottom: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.5);
  padding: 0.5rem;
  font-size: 1.2rem;
}

.image-viewer-name {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.5);
  padding: 0.5rem;
  font-size: 1.2rem;
}

.modal-dialog:has(.image-viewer) {
  height: calc(100% - 3.5rem);

  .modal-content {
    height: 100%;
  }
}
</style>
